import React, { useEffect, useRef, useState } from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledAboutSection = styled.section`
  max-width: 1200px;
  margin: 0 auto;

  .bento-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: auto auto;
    gap: 20px;
    margin-top: 40px;

    @media (max-width: 900px) {
      grid-template-columns: minmax(0, 1fr);
      grid-template-rows: auto;
    }
  }

  .bento-item {
    background-color: var(--surface-light);
    border: 1px solid var(--text-dark);
    border-radius: var(--border-radius-lg);
    padding: 40px;
    transition: var(--transition);
    overflow: hidden;
    position: relative;

    @media (max-width: 480px) {
      padding: 25px;
    }

    &:hover {
      border-color: var(--accent-main);
      transform: translateY(-5px);
      box-shadow: 0 20px 40px -15px rgba(0, 0, 0, 0.8);
    }
  }

  .item-main {
    grid-column: span 2;
    grid-row: span 2;
    display: flex;
    flex-direction: column;
    justify-content: center;

    h3 {
      font-size: var(--fz-heading);
      color: var(--text-main);
      margin-bottom: 30px;

      @media (max-width: 480px) {
        font-size: clamp(30px, 8vw, 40px);
      }
    }

    p {
      font-size: var(--fz-lg);
      line-height: 1.6;
      color: var(--text-muted);
    }

    @media (max-width: 900px) {
      grid-column: span 1;
      grid-row: span 1;
    }
  }

  .item-pic {
    grid-column: span 1;
    grid-row: span 1;
    padding: 0;
    border: none;
    background: transparent;
    overflow: visible; /* Allows the offset border to show */

    &:hover {
      transform: none;
      box-shadow: none;
    }

    .wrapper {
      display: block;
      position: relative;
      width: 100%;
      height: 100%;
      border-radius: var(--border-radius-lg);

      .img {
        position: relative;
        border-radius: var(--border-radius-lg);
        transition: var(--transition);
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    @media (max-width: 900px) {
      height: 400px;
    }
  }

  .item-stat {
    grid-column: span 1;
    grid-row: span 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 25px;

    .stat-block {
      display: flex;
      flex-direction: column;

      h4 {
        font-size: var(--fz-xxl);
        color: var(--accent-main);
        margin: 0 0 5px 0;
        font-family: var(--font-mono);
      }
      span {
        font-size: var(--fz-md);
        color: var(--text-muted);
        font-weight: 500;
      }
    }
  }
`;

const useCountUp = (end, duration = 2000, start = 0) => {
  const [count, setCount] = useState(start);
  const countRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        if (countRef.current) {
          observer.unobserve(countRef.current);
        }
      }
    });

    if (countRef.current) {
      observer.observe(countRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (isVisible) {
      let startTimestamp = null;
      const step = timestamp => {
        if (!startTimestamp) startTimestamp = timestamp;
        const progress = Math.min((timestamp - startTimestamp) / duration, 1);
        const easeOut = 1 - Math.pow(1 - progress, 4);
        setCount(Math.floor(easeOut * (end - start) + start));
        if (progress < 1) {
          window.requestAnimationFrame(step);
        } else {
          setCount(end);
        }
      };
      window.requestAnimationFrame(step);
    }
  }, [isVisible, end, duration, start]);

  return { count, countRef };
};

const formatStat = val => {
  if (val >= 1000000) return Math.floor(val / 1000000) + 'M';
  if (val >= 1000) return Math.floor(val / 1000) + 'K';
  return val;
};

const AnimatedStat = ({ end, suffix = '', prefix = '', duration = 2000, formatter }) => {
  const { count, countRef } = useCountUp(end, duration);
  const displayValue = formatter ? formatter(count) : count;
  return (
    <h4 ref={countRef}>
      {prefix}
      {displayValue}
      {suffix}
    </h4>
  );
};

const About = () => {
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, []);

  return (
    <StyledAboutSection id="about" ref={revealContainer}>
      <h2 className="medium-heading">About Me</h2>

      <div className="bento-grid">
        <div className="bento-item item-main">
          <h3>Architecting Digital Realities.</h3>
          <p>
            Hello! I’m a software engineer passionate about building scalable, AI-powered web
            applications that solve real business problems. Currently a Founding Engineer at{' '}
            <strong>Incresco Technology Solutions</strong>, I architect complex systems ranging from
            cloud-native platforms to autonomous agent pipelines.
          </p>
          <p>
            My journey started with building simple apps out of curiosity, but things got real when
            I began working on a multi-tenant SaaS CRM used by enterprise teams. From shipping GPT-4
            powered scoring systems to creating AI assistants that cut manual work by hours, I’ve
            learned what it takes to take ideas from zero to production.
          </p>
          <p>
            When I’m not coding, I’m usually exploring new AI tools, refining something I’ve already
            built, or figuring out how to automate workflows that shouldn’t need humans in the first
            place. I’m always excited to work on products that push boundaries, scale to real users,
            and make technology feel almost invisible.
          </p>
        </div>

        <div className="bento-item item-pic">
          <div className="wrapper">
            <StaticImage
              className="img"
              src="../../images/me.jpg"
              quality={95}
              formats={['AUTO', 'WEBP', 'AVIF']}
              alt="Headshot"
            />
          </div>
        </div>

        <div className="bento-item item-stat">
          <div className="stat-block">
            <AnimatedStat end={1} suffix="+" />
            <span>Years of Experience</span>
          </div>
          <div className="stat-block">
            <AnimatedStat end={5} suffix="+" />
            <span>Projects Delivered</span>
          </div>
          <div className="stat-block">
            <AnimatedStat end={1000000} suffix="+" formatter={formatStat} />
            <span>Records Processed</span>
          </div>
          <div className="stat-block">
            <AnimatedStat end={50000} suffix="+" formatter={formatStat} />
            <span>Users Supported</span>
          </div>
        </div>
      </div>
    </StyledAboutSection>
  );
};

export default About;
